/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ComponentItemFinderListItem } from '@gh/builder-shared';
import {
  ComponentDetailsDto,
  ComponentItemDto,
  Formula,
  isFormulaEmpty,
  ItemDetailsDto,
  TakeOffParamSharedListItemDto,
  UomSharedListItemDto,
} from '@gh/builder-shared-data';
import { bindUniqueId, hasEntityStateErrors } from '@gh/common-shared';
import {
  WithDirty,
  withDisplayAsNameAndDescription,
  WithEntityState,
  WithUniqueId,
} from '@gh/common-shared-data';
import { cast } from '@gh/core-util';
import { isEmpty, isNil, map, omit, pick } from 'lodash';

export type ItemDetails = ItemDetailsDto & {
  isFormulaNeedPropagation: boolean;
  oldFormula?: Formula;
};
export type UomSharedListItem = UomSharedListItemDto;
export type TakeOffParamSharedListItem = TakeOffParamSharedListItemDto;

export type ComponentItem = WithUniqueId<WithEntityState<WithDirty<ComponentItemDto>>>;
export type ComponentDetails = ComponentDetailsDto & {
  items: ComponentItem[];
  isFormulaNeedPropagation: boolean;
  oldFormula?: Formula;
};

export interface ComponentOrItem {
  formula?: Formula;
  picture?: any;
  memo: string;
}

export function newItemDetails(): ItemDetails {
  return {
    isSalesTax: false,
    editLocked: false,
    active: true,
    isFormulaNeedPropagation: false,
  };
}

export function newComponentDetails(): ComponentDetails {
  return {
    editLocked: false,
    active: true,
    items: [],
    isFormulaNeedPropagation: false,
  };
}

export const whenComponentItemInvalid = hasEntityStateErrors;
export const whenNoItems = (details: ComponentDetails) => isEmpty(details.items);

export function needsFormulaPropagation(details: ComponentDetails | ItemDetails): boolean {
  return !isNil(details.id) && details.isFormulaNeedPropagation
    && !(isFormulaEmpty(details.formula) && isFormulaEmpty(details.oldFormula));
}

export function whenItemsInvalid(details: ComponentDetails): boolean {
  const { items } = details;

  return whenNoItems(details) || items.some(whenComponentItemInvalid);
}

export function componentDetailsFromDto(dto: ComponentDetailsDto): ComponentDetails {
  return {
    ...dto,
    isFormulaNeedPropagation: false,
    oldFormula: dto.formula,
    items: (dto.items || []).map(componentItemFromDto),
  };
}

export function componentDetailsToDto(details: ComponentDetails): ComponentDetailsDto {
  return cast<ComponentDetailsDto>(omit(
    {
      ...details,
      items: <ComponentItemDto[]>map(details.items, componentItemToDto),
    },
    ['isFormulaNeedPropagation', 'oldFormula']));
}

export function componentItemToDto(item: ComponentItem): ComponentItemDto {
  const { costCenter } = item;
  return <ComponentItemDto>{
    ...omit(item, 'isDirty', 'entityState', 'uniqueId', 'costCenter', 'extension'),
    costCenter: costCenter && omit(costCenter, 'display'),
  };
}

export function componentItemFromDto(item: ComponentItemDto): ComponentItem {
  return bindUniqueId({
    ...item,
    isDirty: false,
    costCenter: item.costCenter ? withDisplayAsNameAndDescription(item.costCenter) : void 0,
  });
}

export function componentItemFromFinder(item: ComponentItemFinderListItem): ComponentItem {
  return bindUniqueId({
// @ts-expect-error Legacy typing suppression
    itemId: item.id,
    ...<ComponentItem>pick(item, 'code', 'name', 'cost', 'uomCode', 'active'),
    quantity: 1,
    extension: item.cost,
  });
}

export function itemDetailsFromDto(dto: ItemDetailsDto): ItemDetails {
  return { ...dto, isFormulaNeedPropagation: false, oldFormula: dto.formula };
}

export function itemDetailsToDto(details: ItemDetails): ItemDetailsDto {
  return cast<ItemDetailsDto>(omit(details, ['isFormulaNeedPropagation', 'oldFormula']));
}
